import forge from 'apis/forge';
import Icon, { IconType } from 'components/Icon';
import { McpToken } from 'dragonmouse-shared/api/forge';
import { useCallback, useEffect, useState } from 'react';
import { useTheme } from 'styled-components';
import * as P from '../styles';
import ConnectModal from './ConnectModal';
import * as S from './styles';

const formatLastUsed = (lastUsedAt: string | null): string => {
	if (!lastUsedAt) return 'Never used';
	const then = new Date(lastUsedAt).getTime();
	if (Number.isNaN(then)) return 'Never used';
	const diffMs = Date.now() - then;
	const minutes = Math.floor(diffMs / 60_000);
	if (minutes < 1) return 'Used just now';
	if (minutes < 60) return `Used ${minutes}m ago`;
	const hours = Math.floor(minutes / 60);
	if (hours < 24) return `Used ${hours}h ago`;
	const days = Math.floor(hours / 24);
	return `Used ${days}d ago`;
};

const McpIntegration = () => {
	const theme = useTheme();
	const [tokens, setTokens] = useState<McpToken[] | null>(null);
	const [modalOpen, setModalOpen] = useState(false);

	const refresh = useCallback(async () => {
		const resp = await forge.listMcpTokens();
		setTokens(resp.tokens);
	}, []);

	useEffect(() => {
		void refresh();
	}, [refresh]);

	//----------------------------------------------------------------------
	// HANDLERS
	//----------------------------------------------------------------------

	const onOpenModal = () => setModalOpen(true);
	const onCloseModal = () => setModalOpen(false);

	const onRevoke = async (id: string) => {
		await forge.revokeMcpToken(id);
		await refresh();
	};

	//----------------------------------------------------------------------
	// RENDERING
	//----------------------------------------------------------------------

	const isConnected = (tokens?.length ?? 0) > 0;

	return (
		<>
			<P.IntegrationRow>
				<P.IntegrationInfo>
					<Icon type={IconType.MOONCHILD} width={20} height={20} color={theme.gray[800]} />
					<P.IntegrationName>Moonchild MCP</P.IntegrationName>
					{isConnected && <P.IntegrationStatus>Connected</P.IntegrationStatus>}
				</P.IntegrationInfo>
				<P.IntegrationButton onClick={onOpenModal}>
					{isConnected ? 'Connect another' : 'Connect'}
				</P.IntegrationButton>
			</P.IntegrationRow>
			{tokens && tokens.length > 0 && (
				<S.TokenGroup>
					<S.TokenList>
						{tokens.map((t) => (
							<S.TokenRow key={t.id}>
								<S.TokenInfo>
									<S.TokenName>{t.name}</S.TokenName>
									<S.TokenMeta>
										{t.tokenPrefix}… · {formatLastUsed(t.lastUsedAt)}
									</S.TokenMeta>
								</S.TokenInfo>
								<S.RevokeButton onClick={() => onRevoke(t.id)}>Revoke</S.RevokeButton>
							</S.TokenRow>
						))}
					</S.TokenList>
				</S.TokenGroup>
			)}
			{modalOpen && <ConnectModal onClose={onCloseModal} onTokenCreated={refresh} />}
		</>
	);
};

export default McpIntegration;
